import React from 'react';
import { useDispatch } from 'react-redux';

import { ArrowDown } from '@travel/icons/ui';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';
import { cx } from '@travel/utils';

import StyledSelectBox from 'components/StyledSelectBox';
import { DEFAULT_LIMIT } from 'pages/ProviderListPage/constants';
import { FIRST_PAGE_PARAMS } from 'pages/ProviderListPage/resolver';

import { pushLocation } from 'store/__router/actions';

import { PROVIDER_INFO_SORT_OPTIONS, SORT_OPTIONS } from 'constants/provider';
import paths from 'core/universalRouter/paths';
import useLabelSelectBoxOptions from 'hooks/useLabelSelectBoxOptions';
import type { SearchFormQuery } from 'ProviderList-Types';

import styles from './sortSelectBox.module.scss';

type Props = {
  className?: string;
  /** Current search param object */
  params: { pageIdentifier: string };
  /** Current search query object */
  query: SearchFormQuery;
  /** on what parent page this component lives */
  onPage?: 'providerList' | 'providerInfo';
  /** unSubmit filter condition change  */
  newQuery?: SearchFormQuery;
  popupWrapperClass?: string;
  onAppearanceChanged?: (isOpen: boolean) => void;
};

/**
 * Currently, this component is being used on TL/PC only
 */
function SortSelectBox(props: Props) {
  const { onPage = 'providerList', query, className, params, newQuery, popupWrapperClass } = props;

  const dispatch = useDispatch();
  const isProviderInfo = onPage === 'providerInfo';
  const sortOptions = useLabelSelectBoxOptions(
    Object.values(isProviderInfo ? PROVIDER_INFO_SORT_OPTIONS : SORT_OPTIONS),
  );

  const validSortValues = new Set(sortOptions.map((sortOption) => sortOption.value));

  const defaultValue = isProviderInfo
    ? PROVIDER_INFO_SORT_OPTIONS['price_low_to_high'].value
    : SORT_OPTIONS['recommended'].value;
  const value = query.sortKey && validSortValues.has(query.sortKey) ? query.sortKey : defaultValue;

  const onSelectBoxChanged = (item?: { value: string }) => {
    const sortKey = item?.value;
    if (isProviderInfo) {
      dispatch(
        pushLocation({
          pathname: paths.providerInfo.pathResolver(params.pageIdentifier),
          search: toSEOSearchString({ ...query, sortKey }),
        }),
      );
    } else {
      const searchQuery = newQuery || query;
      dispatch(
        pushLocation({
          pathname: paths.providerList.pathResolver(params.pageIdentifier),
          search: toSEOSearchString({ ...searchQuery, sortKey, ...FIRST_PAGE_PARAMS }),
        }),
      );
    }
  };

  return (
    <StyledSelectBox
      name="sortSelectBox"
      className={cx(className, styles.wrapper)}
      listClassName={cx(styles.popup, popupWrapperClass)}
      options={sortOptions}
      onChange={onSelectBoxChanged}
      onAppearanceChanged={props?.onAppearanceChanged}
      dropdownIcon={<ArrowDown className={styles.displayIcon} color="cilantro" size={12} />}
      value={value}
    />
  );
}

export default React.memo(SortSelectBox);
